import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import api from '../services/api';
import Swal from 'sweetalert2';
import { FiX, FiDownload } from 'react-icons/fi';

const ParcoursAcademiqueModal = ({ isOpen, onClose, eleveId, eleveData }) => {
  const [parcours, setParcours] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && eleveId) {
      fetchParcours();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, eleveId]);

  const fetchParcours = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/eleves/${eleveId}/parcours-academique`);
      if (res.data.success) {
        setParcours(res.data.data || []);
      } else {
        Swal.fire('Erreur', 'Impossible de charger le parcours académique', 'error');
      }
    } catch (error) {
      console.error('Erreur lors du chargement du parcours:', error);
      Swal.fire('Erreur', 'Impossible de charger le parcours académique', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatNote = (note) => {
    if (note === null || note === undefined) return '-';
    return typeof note === 'number' ? note.toFixed(2) : note;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Parcours académique"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          maxWidth: '95vw',
          maxHeight: '95vh',
          width: '100%',
          margin: 'auto',
          borderRadius: 12,
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Parcours académique
              </h2>
              {eleveData && (
                <p className="text-sm text-gray-600 mt-1">
                  {eleveData.nom} {eleveData.prenom}
                  {eleveData.matricule && ` - Matricule: ${eleveData.matricule}`}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              title="Fermer"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : parcours.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucun parcours académique disponible pour cet élève.
            </div>
          ) : (
            <div className={`grid gap-6 ${parcours.length === 1 ? 'grid-cols-1' : parcours.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {parcours.map((item, index) => {
              const { inscription, periodes, matieres, moyenne_annuelle_globale } = item;
              return (
                <div key={inscription.inscription_id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="bg-primary-600 text-white px-4 py-3">
                    <h3 className="font-semibold text-lg">
                      {inscription.annee_scolaire}
                    </h3>
                    <p className="text-sm text-primary-100">
                      {inscription.classe} - {inscription.niveau}
                    </p>
                    {inscription.enseignement && (
                      <p className="text-xs text-primary-200 mt-1">
                        {inscription.enseignement}
                      </p>
                    )}
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                            Matière
                          </th>
                          {periodes.map((periode) => (
                            <th key={periode.id} className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                              {periode.abrev || periode.periode}
                            </th>
                          ))}
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-100">
                            Moy. Ann.
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {matieres.length === 0 ? (
                          <tr>
                            <td colSpan={periodes.length + 2} className="px-4 py-4 text-center text-gray-500 text-sm">
                              Aucune matière disponible
                            </td>
                          </tr>
                        ) : (
                          <>
                            {matieres.map((matiere) => (
                              <tr key={matiere.matiere_id} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-xs font-medium text-gray-900 sticky left-0 bg-white z-10 whitespace-nowrap">
                                  {matiere.abbrev_matiere || matiere.lib_matiere}
                                </td>
                                {periodes.map((periode) => {
                                  const moyenne = matiere.moyennes_par_periode[periode.id];
                                  return (
                                    <td key={periode.id} className="px-2 py-2 text-center text-xs text-gray-700">
                                      {formatNote(moyenne?.valeur)}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2 text-center text-xs font-semibold text-gray-900 bg-gray-50">
                                  {formatNote(matiere.moyenne_annuelle)}
                                </td>
                              </tr>
                            ))}
                            {/* Ligne résumé avec moyenne annuelle globale */}
                            <tr className="bg-primary-50 border-t-2 border-primary-200">
                              <td className="px-3 py-3 text-xs font-bold text-gray-900 sticky left-0 bg-primary-50 z-10">
                                MOYENNE ANNUELLE
                              </td>
                              {periodes.map((periode) => (
                                <td key={periode.id} className="px-2 py-3 text-center text-xs font-semibold text-gray-700">
                                  -
                                </td>
                              ))}
                              <td className="px-3 py-3 text-center text-sm font-bold text-primary-700 bg-primary-100">
                                {formatNote(moyenne_annuelle_globale)}
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={async () => {
              try {
                setLoading(true);
                
                // Utiliser fetch directement pour éviter les problèmes avec axios et blob
                const token = localStorage.getItem('token');
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
                const response = await fetch(`${apiUrl}/eleves/${eleveId}/parcours-academique/pdf`, {
                  method: 'GET',
                  headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                    'Accept': 'application/pdf'
                  }
                });
                
                if (!response.ok) {
                  // Si ce n'est pas OK, essayer de lire comme JSON
                  const errorText = await response.text();
                  try {
                    const errorData = JSON.parse(errorText);
                    Swal.fire('Erreur', errorData.message || 'Impossible de générer le PDF', 'error');
                  } catch {
                    Swal.fire('Erreur', `Erreur ${response.status}: ${response.statusText}`, 'error');
                  }
                  return;
                }
                
                // Vérifier le Content-Type
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/pdf')) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  const fileName = `parcours-academique-${eleveData?.nom || 'eleve'}-${eleveData?.prenom || ''}.pdf`;
                  link.setAttribute('download', fileName);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  // Libérer la mémoire après un court délai
                  setTimeout(() => window.URL.revokeObjectURL(url), 100);
                  Swal.fire({
                    icon: 'success',
                    title: 'Succès',
                    text: 'PDF généré avec succès',
                    timer: 3000,
                    showConfirmButton: false
                  });
                } else {
                  // Si ce n'est pas un PDF, c'est probablement une erreur JSON
                  const text = await response.text();
                  try {
                    const errorData = JSON.parse(text);
                    Swal.fire('Erreur', errorData.message || 'Impossible de générer le PDF', 'error');
                  } catch {
                    Swal.fire('Erreur', 'Réponse inattendue du serveur', 'error');
                  }
                }
              } catch (error) {
                console.error('Erreur lors de la génération du PDF:', error);
                Swal.fire('Erreur', 'Impossible de générer le PDF. Vérifiez votre connexion.', 'error');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading || parcours.length === 0}
            className="flex items-center space-x-2 px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload size={18} />
            <span>Générer PDF</span>
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ParcoursAcademiqueModal;

